import { useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UserAvatar from "./UserAvatar";
import { logout } from "../services/Logout";
import { LocaleFields } from "./LocaleSwitcher";

// Phone/tablet navigation drawer. React-controlled (not a Bootstrap offcanvas) so it can close
// itself on navigation, Escape and backdrop click.
export default function MobileDrawer({ open, onClose }) {
    const { t } = useTranslation();
    const location = useLocation();
    const { user, accessToken } = useSelector((state) => state.authReducer);
    const chatlist = useSelector((state) => state.chatListReducer);
    const unread = chatlist?.data?.reduce?.((sum, c) => sum + (Number(c?.unread_count) || 0), 0) || 0;
    const closeRef = useRef(null);

    const isTeacher = accessToken && user?.role === 'teacher';
    const isStudent = accessToken && user?.role === 'student';

    // close whenever the route changes
    useEffect(() => {
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    // Escape to close, lock page scroll while open, move focus into the drawer
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => e.key === 'Escape' && onClose();
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    const explore = [
        { to: '/teachers', icon: 'fa-chalkboard-user', label: t('header.find_tutors') },
        { to: '/all-courses', icon: 'fa-book-open', label: t('header.all_courses') },
        isTeacher
            ? { to: '/dashboard/my-courses', icon: 'fa-rectangle-list', label: t('header.your_courses') }
            : { to: '/teach', icon: 'fa-graduation-cap', label: t('header.become_a_tutor') },
    ];

    const account = isTeacher ? [
        { to: '/dashboard/tutor', icon: 'fa-grid-2', label: t('header.dashboard'), end: true },
        { to: '/dashboard/chat', icon: 'fa-message-lines', label: t('header.messages'), badge: unread },
        { to: '/dashboard/all-enrollment-courses', icon: 'fa-list', label: t('header.classes') },
        { to: '/dashboard/live-room', icon: 'fa-video', label: t('header.live_room') },
        { to: '/dashboard/earnings', icon: 'fa-wallet', label: t('header.earning_history') },
        { to: '/dashboard/transactions', icon: 'fa-chart-bar', label: t('header.transaction_history') },
        { to: '/dashboard/withdraw', icon: 'fa-money-bill-transfer', label: t('header.withdraw_history') },
        { to: '/dashboard/subscription', icon: 'fa-crown', label: t('header.subscription') },
        { to: '/dashboard/settings', icon: 'fa-user-gear', label: t('header.settings') },
        { to: '/dashboard/help', icon: 'fa-circle-info', label: t('header.help') },
    ] : isStudent ? [
        { to: '/home/student', icon: 'fa-grid-2', label: t('header.dashboard'), end: true },
        { to: '/home/chat', icon: 'fa-message-lines', label: t('header.messages'), badge: unread },
        { to: '/home/my-lessons', icon: 'fa-chalkboard-user', label: t('header.my_lessons') },
        { to: '/home/save-course', icon: 'fa-heart', label: t('header.saved_courses') },
        { to: '/home/transactions', icon: 'fa-receipt', label: t('header.transaction_history') },
        { to: '/home/settings/profile', icon: 'fa-user-gear', label: t('header.settings') },
        { to: '/home/help', icon: 'fa-circle-info', label: t('header.help') },
    ] : [];

    // closes even when tapping the link of the page you're already on (no route change then)
    const renderLink = (item) => (
        <li key={item.to}>
            <NavLink to={item.to} end={item.end} className="md-link" onClick={onClose}>
                <i className={`fa-light ${item.icon}`} />
                <span>{item.label}</span>
                {item.badge > 0 && <span className="md-badge">{item.badge}</span>}
            </NavLink>
        </li>
    );

    return (
        <div className={`md-root ${open ? 'is-open' : ''}`} aria-hidden={!open}>
            <div className="md-backdrop" onClick={onClose} />
            <aside className="md-panel" role="dialog" aria-modal="true" aria-label={t('header.toggle_navigation')}>
                <div className="md-head">
                    <Link to="/" className="md-logo" onClick={onClose}>
                        <img src="/assets/img/logo.svg" alt="xller" />
                    </Link>
                    <button type="button" ref={closeRef} className="md-close" onClick={onClose} aria-label={t('header.close')}>
                        <i className="fa-light fa-xmark" />
                    </button>
                </div>

                <div className="md-body">
                    {accessToken ? (
                        <div className="md-profile">
                            <UserAvatar src={user?.avatar} name={user?.name} size={48} />
                            <div className="md-profile-text">
                                <strong>{user?.name}</strong>
                                <span>
                                    {isTeacher ? t('tutorNav.teacher') : t('tutorNav.student')}
                                    {isTeacher && <> · ${user?.balance ?? 0}</>}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="md-auth">
                            <Link to="/login" className="md-btn md-btn-outline" onClick={onClose}>{t('header.login')}</Link>
                            <Link to="/signup-student" className="md-btn md-btn-primary" onClick={onClose}>{t('header.sign_up')}</Link>
                        </div>
                    )}

                    <p className="md-label">{t('header.explore')}</p>
                    <ul className="md-list">{explore.map(renderLink)}</ul>

                    {account.length > 0 && <>
                        <p className="md-label">{t('header.my_account')}</p>
                        <ul className="md-list">{account.map(renderLink)}</ul>
                    </>}

                    <p className="md-label">{t('header.language_currency')}</p>
                    <div className="md-locale">
                        <LocaleFields idPrefix="md" />
                    </div>
                </div>

                {accessToken &&
                    <div className="md-foot">
                        <button type="button" className="md-logout" onClick={logout}>
                            <i className="fa-light fa-arrow-right-from-bracket" />
                            <span>{t('header.logout')}</span>
                        </button>
                    </div>
                }
            </aside>
        </div>
    );
}
